/**
 * Cloudflare Turnstile widget — rendered ONLY when `VITE_TURNSTILE_SITE_KEY` is configured (the cloud
 * captcha provider). It lazy-loads the Turnstile script, renders the challenge, and reports the minted
 * token via `onToken`. When no site key is set (the default — the built-in PoW provider answers), this
 * renders nothing and the page's `requestLabAccess` transparently solves the stateless PoW instead.
 *
 * Deliberately dependency-free (no @marsidev wrapper) and side-effect-guarded so it is inert in dev/test
 * and never blocks the existing E2E (which runs with no site key → the component returns null).
 */
import { useEffect, useRef } from "react";
import { TURNSTILE_SITE_KEY } from "@/api";

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      remove: (id: string) => void;
    };
  }
}

function ensureScript(): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve) => existing.addEventListener("load", () => resolve(), { once: true }));
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile: script load failed"));
    document.head.appendChild(s);
  });
}

export function TurnstileWidget({ onToken }: { onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !ref.current) return;
    let widgetId: string | undefined;
    let cancelled = false;
    ensureScript()
      .then(() => {
        if (cancelled || !window.turnstile || !ref.current) return;
        widgetId = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => onToken(token),
        });
      })
      .catch(() => {
        /* script unreachable — the server gate will fall back / reject; nothing to render */
      });
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken]);

  if (!TURNSTILE_SITE_KEY) return null;
  return <div ref={ref} className="nv-auth__turnstile" style={{ marginBlockStart: "var(--nv-s-4)" }} />;
}
